import { useMemo, useState } from "react";

import { walkDer, type DerNode } from "../lib/der";

interface Selection {
  offset: number;
  length: number;
}

export function Asn1HexPanel({ bytes }: { bytes: Uint8Array }) {
  const [open, setOpen] = useState(false);
  return (
    <section className="panel">
      <h3>
        <button
          type="button"
          className="disclosure"
          onClick={() => setOpen(!open)}
          aria-expanded={open}
        >
          {open ? "▾" : "▸"} Raw ASN.1
        </button>
      </h3>
      {open && <Asn1HexBody bytes={bytes} />}
    </section>
  );
}

function Asn1HexBody({ bytes }: { bytes: Uint8Array }) {
  const [selection, setSelection] = useState<Selection | null>(null);
  const tree = useMemo(() => {
    try {
      return walkDer(bytes);
    } catch {
      return null;
    }
  }, [bytes]);

  if (!tree) return <p className="muted">Could not build the ASN.1 tree.</p>;
  return (
    <div className="asn1-split">
      <div className="asn1-tree" role="tree">
        <TreeNode node={tree} depth={0} selection={selection} onSelect={setSelection} />
      </div>
      <HexPane bytes={bytes} selection={selection} />
    </div>
  );
}

function TreeNode({
  node,
  depth,
  selection,
  onSelect,
}: {
  node: DerNode;
  depth: number;
  selection: Selection | null;
  onSelect: (s: Selection) => void;
}) {
  const [collapsed, setCollapsed] = useState(depth > 3);
  const selected =
    selection?.offset === node.offset && selection?.length === node.length;
  return (
    <div className="tree-node" style={{ marginLeft: depth === 0 ? 0 : 14 }}>
      <div className={`tree-row${selected ? " selected" : ""}`}>
        {node.children.length > 0 && (
          <button
            type="button"
            className="tree-toggle"
            onClick={() => setCollapsed(!collapsed)}
            aria-label={collapsed ? "expand" : "collapse"}
          >
            {collapsed ? "▸" : "▾"}
          </button>
        )}
        <button
          type="button"
          className="tree-label"
          onClick={() => onSelect({ offset: node.offset, length: node.length })}
        >
          <span className="tree-tag">{node.tagName}</span>
          <span className="tree-meta mono">
            @{node.offset}+{node.length}
          </span>
          {node.preview && <span className="tree-preview mono">{node.preview}</span>}
        </button>
      </div>
      {!collapsed &&
        node.children.map((child) => (
          <TreeNode
            key={child.offset}
            node={child}
            depth={depth + 1}
            selection={selection}
            onSelect={onSelect}
          />
        ))}
    </div>
  );
}

const ROW = 16;
const MAX_BYTES = 64 * 1024;

function HexPane({
  bytes,
  selection,
}: {
  bytes: Uint8Array;
  selection: Selection | null;
}) {
  const truncated = bytes.length > MAX_BYTES;
  const view = truncated ? bytes.subarray(0, MAX_BYTES) : bytes;
  const rows: number[] = [];
  for (let i = 0; i < view.length; i += ROW) rows.push(i);
  const inSel = (i: number) =>
    selection && i >= selection.offset && i < selection.offset + selection.length;

  return (
    <div className="hex-pane mono" aria-label="hex dump">
      {rows.map((start) => (
        <div key={start} className="hex-row">
          <span className="hex-offset">{start.toString(16).padStart(6, "0")}</span>
          <span className="hex-bytes">
            {Array.from(view.subarray(start, start + ROW)).map((b, j) => (
              <span
                key={j}
                className={inSel(start + j) ? "hex-byte hl" : "hex-byte"}
              >
                {b.toString(16).padStart(2, "0")}
              </span>
            ))}
          </span>
          <span className="hex-ascii">
            {Array.from(view.subarray(start, start + ROW))
              .map((b) => (b >= 0x20 && b < 0x7f ? String.fromCharCode(b) : "·"))
              .join("")}
          </span>
        </div>
      ))}
      {truncated && (
        <p className="muted">…truncated at {MAX_BYTES} bytes for display.</p>
      )}
    </div>
  );
}
